import { Typography, Badge } from '@frontend/design_system'
import { useAppSelector } from 'redux/hooks'
import { authIsLoadedSelector, userDataSelector } from 'redux/login/selectors'
import { CardType, CardOption } from './types'
import { REGISTRATION_STATUSES } from 'api/auth/constants'
import { URLS } from 'constants/urls'
import {
  ProfileIcon,
  CarIcon,
  LocationIcon,
  PriceListIcon,
  DocumentsIcon,
} from 'ui/icons'
import {
  CARS_INFO,
  PROFILE_INFO,
  LOCATION_INFO,
  CARD_BLOCK_TITLE,
  TERMS_INFO,
  PRICE_LIST_INFO,
  PENDING_FOR_APPROVAL_BADGE,
  DOCUMENTS_INFO,
} from 'texts/pendingForApproval'
import { PendingCard } from './components/PendingCard'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'
import { useEffect } from 'react'
import { Container } from 'ui/molecules/Container'
import { MENU_NAMES } from 'texts/menuNames'
import styles from './styles.module.scss'

const cardOptions: CardOption[] = [
  {
    type: CardType.PROFILE,
    Icon: ProfileIcon,
    title: PROFILE_INFO.title,
    description: PROFILE_INFO.description,
    path: URLS.PROFILE + URLS.DETAILS,
  },
  {
    type: CardType.DOCUMENTS,
    Icon: DocumentsIcon,
    title: DOCUMENTS_INFO.title,
    description: DOCUMENTS_INFO.description,
    path: URLS.DOCUMENTS,
  },
  {
    type: CardType.LOCATION,
    Icon: LocationIcon,
    title: LOCATION_INFO.title,
    description: LOCATION_INFO.description,
    path: URLS.LOCATIONS + URLS.LIST_OF_LOCATIONS,
  },
  {
    type: CardType.CARS,
    Icon: CarIcon,
    title: CARS_INFO.title,
    description: CARS_INFO.description,
    path: URLS.FLEET + URLS.VEHICLES,
  },
  {
    type: CardType.PRICE_LIST,
    Icon: PriceListIcon,
    title: PRICE_LIST_INFO.title,
    description: PRICE_LIST_INFO.description,
    path: URLS.FLEET + URLS.PRICE_LIST,
  },
  {
    type: CardType.TERMS,
    Icon: CarIcon,
    title: TERMS_INFO.title,
    description: TERMS_INFO.description,
    path: URLS.TERMS_AND_CONDITIONS + URLS.RENTAL_INCLUDES,
  },
]

const BADGE_MESSAGE = {
  [REGISTRATION_STATUSES.INCOMPLETE]: PENDING_FOR_APPROVAL_BADGE.INCOMPLETE,
  [REGISTRATION_STATUSES.REVIEW]: (
    <Typography color="yellow800" name="body1WMedium">
      {PENDING_FOR_APPROVAL_BADGE.REVIEW.INFORMATION_SUBMITTED}
      <Typography color="yellow800" Tag="p" name="body1WMedium">
        {PENDING_FOR_APPROVAL_BADGE.REVIEW.WE_WILL_KEEP_YOU_UPDATED}
      </Typography>
    </Typography>
  ),
  [REGISTRATION_STATUSES.APPROVED]: (
    <Typography color="yellow800" name="body1WMedium">
      {PENDING_FOR_APPROVAL_BADGE.APPROVED.COMPLETE_ALL_REQUIRED_SECTIONS}
      <Typography color="yellow800" Tag="p" name="body1WMedium">
        {PENDING_FOR_APPROVAL_BADGE.APPROVED.NOTE_THAT_OTHER_PAGES_ARE_OPTIONAL}
      </Typography>
    </Typography>
  ),
}

export const PendingForApproval = () => {
  const { sectionsStatuses, isProfileAndDocumentsAlreadyApproved } =
    useAppSelector(userDataSelector)
  const isLoaded = useAppSelector(authIsLoadedSelector)
  const isRequiredSectionsCompleted =
    (sectionsStatuses?.profileStatus === REGISTRATION_STATUSES.APPROVED &&
      sectionsStatuses?.documentsStatus === REGISTRATION_STATUSES.APPROVED) ||
    isProfileAndDocumentsAlreadyApproved

  const { getCompanyInfo, companyId } = useCompanyInfo(false)

  const checkIsAllowedSection = (type: CardType) =>
    [CardType.PROFILE, CardType.DOCUMENTS].includes(type)

  useEffect(() => {
    if (companyId) {
      getCompanyInfo(Number(companyId))
    }
  }, [])

  if (!sectionsStatuses) {
    return null
  }

  const renderBadgeContent = () => {
    if (
      Object.values(sectionsStatuses).some(
        (el) => el === REGISTRATION_STATUSES.REQUESTED
      )
    ) {
      return PENDING_FOR_APPROVAL_BADGE.REQUESTED
    } else {
      return BADGE_MESSAGE[sectionsStatuses.profileStatus]
    }
  }

  return (
    <Container
      currentPageLabel={MENU_NAMES.DASHBOARD}
      title={
        <Typography Tag="div" name="H5" className={styles['container-title']}>
          {MENU_NAMES.DASHBOARD}
        </Typography>
      }
    >
      {isLoaded ? (
        <div className={styles.container} data-testid="pending-for-approval">
          <div className={styles['pending-block']}>
            <Typography name="subtitleWBold">{CARD_BLOCK_TITLE}</Typography>
            <Badge
              message={renderBadgeContent()}
              className={styles.badge}
              type="warning"
              dataTestId="error-badge"
              color="yellow800"
              name="body1WMedium"
            />
            <div className={styles.cards}>
              {cardOptions.map(
                ({ Icon, type, title, description, path }, index) => (
                  <PendingCard
                    key={index}
                    Icon={
                      <Icon
                        size="medium"
                        color={
                          checkIsAllowedSection(type) ||
                          isRequiredSectionsCompleted
                            ? 'blackMaster'
                            : 'grey200'
                        }
                      />
                    }
                    path={path}
                    title={title}
                    description={description}
                    disabled={
                      !(
                        checkIsAllowedSection(type) ||
                        isRequiredSectionsCompleted
                      )
                    }
                    status={sectionsStatuses[type]}
                  />
                )
              )}
            </div>
          </div>
        </div>
      ) : null}
    </Container>
  )
}
